import React, { useCallback, useEffect, useRef, useState } from 'react';
import heroSection from './image/manual/hero-section.png';
import heroLogo from './image/manual/logo.png';
import heroAppleBadge from './image/manual/apple.png';
import heroGoogleBadge from './image/manual/chplay.png';
import metricsStrip from './image/manual/metrics-strip.png';
import metricsCard1 from './image/manual/1.png';
import metricsCard2 from './image/manual/2.png';
import metricsCard3 from './image/manual/3.png';
import evolutionDesktop from './image/manual/evolution-desktop.png';
import phoneConnect from './image/manual/phone-connect.png';
import phoneDeploy from './image/manual/phone-grow.png';
import phoneGrow from './image/manual/phone-deploy.png';
import economySection from './image/manual/economy-section.png';
import xIcon from './image/manual/x.png';

import './App.css';
import analyticsController from './AnalyticsController';
import { useLiveStats } from './useLiveStats';

const appStoreUrl = 'https://apps.apple.com/us/app/dopamine-bitcoin-crypto/id1350234503';
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.kyriakosalexandrou.coinmarketcap';
const defaultPhoneDemoUrl = 'https://youtube.com/shorts/kRcul3zcuHU';
const phoneDemoUrl1 = "https://youtube.com/shorts/kRcul3zcuHU";
const phoneDemoUrl2 = "https://youtube.com/shorts/dnosVYbdBks";
const phoneDemoUrl3 = "https://youtube.com/shorts/N2FHuzyGKng";
const MOBILE_BREAKPOINT_QUERY = '(max-width: 620px)';
const ACTION_SLIDES = [
  {
    id: 'action-connect',
    title: 'Connect & Fund',
    subtitle: 'Start trading in seconds.',
    image: phoneConnect,
    demoUrl: phoneDemoUrl1
  },
  {
    id: 'action-deploy',
    title: 'Deploy Strategy',
    subtitle: 'Monetize your trader signal.',
    image: phoneDeploy,
    demoUrl: phoneDemoUrl2
  },
  {
    id: 'action-grow',
    title: 'Scale Your Network',
    subtitle: 'Grow from trader to network owner.',
    image: phoneGrow,
    demoUrl: phoneDemoUrl3
  }
];

const MOBILE_METRICS_BUTTONS = [
  { id: 'action-connect', image: metricsCard1, label: 'Connect and fund', metricType: 'installs' },
  { id: 'action-deploy', image: metricsCard2, label: 'Deploy strategy', metricType: 'users' },
  { id: 'action-grow', image: metricsCard3, label: 'Scale network', metricType: 'events' }
];

const DESKTOP_SLIDE_ORDER = ['features', 'evolution', 'action', 'economy', 'incubated'];
const MOBILE_SLIDE_ORDER = ['features', 'evolution', ...ACTION_SLIDES.map((slide) => slide.id), 'economy', 'incubated'];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toYoutubeEmbedUrl = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = parsed.pathname.replace('/', '');
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname.startsWith('/shorts/')) {
        videoId = parsed.pathname.split('/')[2] || '';
      } else if (parsed.pathname === '/watch') {
        videoId = parsed.searchParams.get('v') || '';
      } else if (parsed.pathname.startsWith('/embed/')) {
        videoId = parsed.pathname.split('/')[2] || '';
      }
    }

    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`
      : url;
  } catch {
    return url;
  }
};

function App() {
  const [loading, setLoading] = useState(true);
  const [exiting, setExiting] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
  });
  const { stats } = useLiveStats();
  const carouselRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const activeSlideRef = useRef(0);

  const activeEmbedUrl = activeVideoUrl ? toYoutubeEmbedUrl(activeVideoUrl) : '';
  const slideOrder = isMobile ? MOBILE_SLIDE_ORDER : DESKTOP_SLIDE_ORDER;
  const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);
  const normalizeSlideId = useCallback((id) => (id === 'how-it-works' ? 'features' : id), []);
  const toEventFragment = useCallback((value = '') => (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
  ), []);

  const trackTouchEvent = useCallback((eventName, href = '') => {
    if (typeof window === 'undefined') {
      return;
    }

    analyticsController.trackEvent(eventName, {
      href,
      path: window.location.pathname
    });
  }, []);

  const resolveLinkEventName = useCallback((href = '') => {
    if (href === appStoreUrl) {
      return 'tap_download_app_store';
    }
    if (href === googlePlayUrl) {
      return 'tap_download_google_play';
    }
    if (href.startsWith('#')) {
      const sectionId = normalizeSlideId(href.slice(1));
      return `tap_nav_${toEventFragment(sectionId || 'section')}`;
    }
    if (href.includes('x.com/myDopamineApp')) {
      return 'tap_open_x_profile';
    }
    return 'tap_external_link';
  }, [normalizeSlideId, toEventFragment]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const media = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const onChange = (event) => setIsMobile(event.matches);

    setIsMobile(media.matches);

    if (media.addEventListener) {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  useEffect(() => {
    activeSlideRef.current = activeSlide;
  }, [activeSlide]);

  const getSlideElements = useCallback(() => {
    const el = carouselRef.current;
    return el ? Array.from(el.querySelectorAll('.carousel-slide')) : [];
  }, []);

  const resolveNearestSlideIndex = useCallback((scrollTop) => {
    const slides = getSlideElements();
    if (!slides.length) {
      return 0;
    }

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetTop - scrollTop);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }, [getSlideElements]);

  const scrollToSlide = useCallback((index, behavior = 'smooth') => {
    const el = carouselRef.current;
    const slides = getSlideElements();
    if (!el || !slides.length) {
      return;
    }

    const nextIndex = clamp(index, 0, slides.length - 1);
    el.scrollTo({ top: slides[nextIndex].offsetTop, behavior });
  }, [getSlideElements]);

  useEffect(() => {
    const maxIndex = slideOrder.length - 1;
    if (activeSlideRef.current <= maxIndex) {
      return;
    }

    setActiveSlide(maxIndex);
    scrollToSlide(maxIndex, 'auto');
  }, [slideOrder, scrollToSlide]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) {
      return undefined;
    }

    let rafId = 0;
    const onScroll = () => {
      rafId = 0;
      const nextIndex = resolveNearestSlideIndex(el.scrollTop);
      if (nextIndex !== activeSlideRef.current) {
        setActiveSlide(nextIndex);
      }
    };

    const onScrollRaf = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(onScroll);
    };

    el.addEventListener('scroll', onScrollRaf, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScrollRaf);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [resolveNearestSlideIndex]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) {
      return undefined;
    }

    let snapTimer = 0;
    const onScrollEnd = () => {
      window.clearTimeout(snapTimer);
      snapTimer = window.setTimeout(() => {
        if (activeVideoUrl) {
          return;
        }

        const slides = getSlideElements();
        if (!slides.length) {
          return;
        }

        const nearestIndex = resolveNearestSlideIndex(el.scrollTop);
        const targetTop = slides[nearestIndex].offsetTop;
        if (Math.abs(el.scrollTop - targetTop) > 2) {
          el.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
      }, 120);
    };

    el.addEventListener('scroll', onScrollEnd, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScrollEnd);
      window.clearTimeout(snapTimer);
    };
  }, [activeVideoUrl, getSlideElements, resolveNearestSlideIndex]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) {
      return undefined;
    }

    let locked = false;
    let unlockTimer = 0;

    const onWheel = (event) => {
      if (activeVideoUrl) {
        return;
      }

      if (event.ctrlKey) {
        return;
      }

      if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) {
        return;
      }

      if (locked) {
        event.preventDefault();
        return;
      }

      const direction = event.deltaY > 0 ? 1 : -1;
      const slideCount = getSlideElements().length;
      if (!slideCount) {
        return;
      }

      const nextIndex = clamp(activeSlideRef.current + direction, 0, slideCount - 1);
      if (nextIndex === activeSlideRef.current) {
        return;
      }

      event.preventDefault();
      locked = true;
      scrollToSlide(nextIndex);
      window.clearTimeout(unlockTimer);
      unlockTimer = window.setTimeout(() => {
        locked = false;
      }, 760);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      window.clearTimeout(unlockTimer);
    };
  }, [activeVideoUrl, getSlideElements, scrollToSlide]);

  useEffect(() => {
    const onResize = () => scrollToSlide(activeSlideRef.current, 'auto');
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [scrollToSlide]);

  useEffect(() => {
    const hash = (window.location.hash || '').replace('#', '');
    const indexFromHash = slideOrder.indexOf(normalizeSlideId(hash));
    if (indexFromHash !== -1) {
      scrollToSlide(indexFromHash, 'auto');
    }
  }, [normalizeSlideId, scrollToSlide, slideOrder]);

  useEffect(() => {
    analyticsController.trackPageView();
  }, []);

  const onCarouselClick = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link) {
      return;
    }

    const href = link.getAttribute('href') || link.href || '';
    trackTouchEvent(resolveLinkEventName(href), href);

    if (!href.startsWith('#')) {
      return;
    }

    const targetId = normalizeSlideId(href.slice(1));
    const targetIndex = slideOrder.indexOf(targetId);
    if (targetIndex === -1) {
      return;
    }

    event.preventDefault();
    scrollToSlide(targetIndex);
  };

  const openDemoVideo = useCallback((slideId, demoUrl) => {
    const href = demoUrl || defaultPhoneDemoUrl;
    trackTouchEvent(`tap_open_demo_${toEventFragment(slideId || 'video')}`, href);
    setActiveVideoUrl(href);
  }, [toEventFragment, trackTouchEvent]);

  const onCarouselDotClick = useCallback((slideId, idx) => {
    trackTouchEvent(`tap_carousel_dot_${toEventFragment(slideId || `index_${idx + 1}`)}`, `#${slideId}`);
    scrollToSlide(idx);
  }, [scrollToSlide, toEventFragment, trackTouchEvent]);

  const closeVideoModal = useCallback((source) => {
    trackTouchEvent(`tap_close_video_modal_${toEventFragment(source || 'unknown')}`, 'video_modal');
    setActiveVideoUrl('');
  }, [toEventFragment, trackTouchEvent]);

  const onVideoModalClick = useCallback((event) => {
    trackTouchEvent('tap_video_modal_content', 'video_modal');
    event.stopPropagation();
  }, [trackTouchEvent]);

  const onCarouselKeyDown = (event) => {
    if (activeVideoUrl) {
      return;
    }

    const key = event.key;
    if (key === 'ArrowDown' || key === 'PageDown' || key === ' ') {
      event.preventDefault();
      scrollToSlide(activeSlideRef.current + 1);
      return;
    }

    if (key === 'ArrowUp' || key === 'PageUp') {
      event.preventDefault();
      scrollToSlide(activeSlideRef.current - 1);
    }
  };

  useEffect(() => {
    const minDelay = 400;
    const fadeOut = 280;
    const start = Date.now();

    const hideLoader = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, minDelay - elapsed);
      setTimeout(() => {
        setExiting(true);
        setTimeout(() => setLoading(false), fadeOut);
      }, remaining);
    };

    if (document.readyState === 'complete') {
      hideLoader();
    } else {
      window.addEventListener('load', hideLoader);
      return () => window.removeEventListener('load', hideLoader);
    }
  }, []);

  useEffect(() => {
    if (!activeVideoUrl) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveVideoUrl('');
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeVideoUrl]);

  return (
    <div className="page-bg" style={{ '--hero-bg': `url(${heroSection})` }}>
      {loading && (
        <div className={`page-loader${exiting ? ' page-loader--out' : ''}`} aria-hidden="true">
          <div className="page-loader-spinner" />
        </div>
      )}
      <main
        ref={carouselRef}
        className="carousel"
        aria-label="Sweet Dopamine landing carousel"
        tabIndex={0}
        onKeyDown={onCarouselKeyDown}
        onClick={onCarouselClick}
      >
        <section className="carousel-slide features-slide" id="features" aria-label="Features">
          <div className="slide-inner">
            <div className="features-stack">
              <section className="panel hero-panel hero-panel--carousel">
                <div className="hero-left-pack">
                  <div className="hero-left-card">
                    <img src={heroLogo} alt="Sweet Dopamine logo" className="hero-logo-mark" />
                    <div className="hero-title-row">
                      <h1 className="hero-title">SWEET DOPAMINE</h1>
                      <span className="hero-pill">Free</span>
                    </div>
                    <p className="hero-subtitle">Autonomous Trading Network.</p>
                    <p className="hero-highlight">Trade with DOPA (AI Agent)</p>
                    <p className="hero-copy">
                      Monetize your strategies.
                      <br />
                      Build your trader network.
                    </p>
                    <div className="hero-stores">
                      <a href={appStoreUrl} target="_blank" rel="noreferrer" className="hero-store-btn" aria-label="Download on the App Store">
                        <img src={heroAppleBadge} alt="" className="hero-store-btn-icon" />
                        <span className="hero-store-btn-text">
                          <span className="hero-store-btn-small">Download the</span>
                          <span className="hero-store-btn-main">App Store</span>
                        </span>
                      </a>
                      <a href={googlePlayUrl} target="_blank" rel="noreferrer" className="hero-store-btn" aria-label="Get it on Google Play">
                        <img src={heroGoogleBadge} alt="" className="hero-store-btn-icon" />
                        <span className="hero-store-btn-text">
                          <span className="hero-store-btn-small">GET IT ON</span>
                          <span className="hero-store-btn-main">Google Play</span>
                        </span>
                      </a>
                    </div>
                    <p className="hero-footnote">Apple Silicon supported (M1+)</p>
                  </div>
                </div>
              </section>

              <section className="panel metrics-panel metrics-panel--compact" id="how-it-works" aria-label="Live network metrics">
                {isMobile ? (
                  <div className="metrics-mobile-stack" aria-label="Quick actions">
                    {MOBILE_METRICS_BUTTONS.map((item) => (
                      <a key={item.id} href={`#${item.id}`} className="metrics-mobile-button" aria-label={`Open ${item.label}`}>
                        <img src={item.image} alt={item.label} className="metrics-mobile-image" loading="lazy" />
                        <span className={`metrics-mobile-stat metrics-mobile-stat--${item.metricType}`} aria-hidden="true">
                          <span className="metrics-mobile-number">
                            {item.metricType === 'installs'
                              ? formatNumber(stats.totalNumberDownloaded)
                              : item.metricType === 'users'
                                ? formatNumber(stats.realtimeUsers)
                                : formatNumber(stats.realtimeEvents)}
                          </span>
                          {item.metricType === 'users' ? (
                            <span className="metrics-mobile-live">(live)</span>
                          ) : (
                            <span className="metrics-mobile-suffix">+</span>
                          )}
                        </span>
                      </a>
                    ))}
                    <p className="sr-only">
                      Total installations: {formatNumber(stats.totalNumberDownloaded)}. Active traders connected: {formatNumber(stats.realtimeUsers)}.
                      Trading events processed: {formatNumber(stats.realtimeEvents)}.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="metrics-media">
                      <img src={metricsStrip} alt="Live network metrics" className="strip-image" loading="lazy" />
                      <div className="metrics-overlay" aria-hidden="true">
                        <div className="metric-value metric-value--installs">
                          <span className="metric-number metric-number--underline">
                            {formatNumber(stats.totalNumberDownloaded)}
                            <span className="metric-plus">+</span>
                          </span>
                        </div>
                        <div className="metric-value metric-value--users">
                          <span className="metric-number">
                            {formatNumber(stats.realtimeUsers)}
                            <span className="metric-live">(live)</span>
                          </span>
                        </div>
                        <div className="metric-value metric-value--events">
                          <span className="metric-number metric-number--underline metric-number--events">
                            {formatNumber(stats.realtimeEvents)}
                            <span className="metric-plus">+</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="sr-only">
                      Total installations: {formatNumber(stats.totalNumberDownloaded)}. Active traders connected: {formatNumber(stats.realtimeUsers)}.
                      Trading events processed: {formatNumber(stats.realtimeEvents)}.
                    </p>
                  </>
                )}
              </section>
            </div>
          </div>
        </section>

        <section className="carousel-slide" id="evolution" aria-label="The evolution of a trader">
          <div className="slide-inner">
            <section className="panel evolution-section">
              <div className="evolution-media" aria-hidden="true">
                <img src={evolutionDesktop} alt="" className="evolution-image" loading="lazy" />
              </div>
              <p className="sr-only">The evolution of a trader</p>
            </section>
          </div>
        </section>

        {isMobile ? (
          ACTION_SLIDES.map((slide) => (
            <section key={slide.id} className="carousel-slide" id={slide.id} aria-label={slide.title}>
              <div className="slide-inner">
                <section className="panel action-panel action-panel--single">
                  <h2>SEE DOPA IN ACTION</h2>
                  <p className="subtitle">{slide.subtitle}</p>
                  <p className="action-step">{slide.title}</p>

                  <button
                    type="button"
                    className="phone-card phone-card--single"
                    aria-label={`Open ${slide.title} demo`}
                    onClick={() => openDemoVideo(slide.id, slide.demoUrl)}
                  >
                    <img src={slide.image} alt="" loading="lazy" />
                    <span className="pulse-ring" aria-hidden="true" />
                  </button>

                  <p className="center-note">You are not just a trader. You are building a trading network.</p>
                </section>
              </div>
            </section>
          ))
        ) : (
          <section className="carousel-slide" id="action" aria-label="See Dopa in action">
            <div className="slide-inner">
              <section className="panel action-panel">
                <h2>SEE DOPA IN ACTION</h2>
                <p className="subtitle">From first trade to network revenue.</p>

                <div className="phone-grid">
                  {ACTION_SLIDES.map((slide, idx) => (
                    <button
                      key={slide.id}
                      type="button"
                      className="phone-card"
                      aria-label={`Open demo ${idx + 1}`}
                      onClick={() => openDemoVideo(slide.id, slide.demoUrl)}
                    >
                      <img src={slide.image} alt="" loading="lazy" />
                      <span className="pulse-ring" aria-hidden="true" />
                    </button>
                  ))}
                </div>

                <p className="center-note">You are not just a trader. You are building a trading network.</p>
              </section>
            </div>
          </section>
        )}

        <section className="carousel-slide" id="economy" aria-label="Economy">
          <div className="slide-inner">
            <section className="panel economy-panel">
              <img src={economySection} alt="User economy" className="economy-image" loading="lazy" />
            </section>
          </div>
        </section>

        <section className="carousel-slide" id="incubated" aria-label="Download Sweet Dopamine">
          <div className="slide-inner">
            <section className="panel cta-panel">
              <div className="cta-scrim" aria-hidden="true" />
              <div className="cta-content">
                <h2 className="cta-title">YOU TRADE. DOPA AMPLIFIES.</h2>
                <p className="cta-copy">
                  More volume -&gt; More influence -&gt; More network power.
                  <br />
                  You are building a trading network.
                </p>

                <div className="cta-stores" aria-label="Download links">
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hero-store-btn cta-store-btn"
                    aria-label="Download on the App Store"
                  >
                    <img src={heroAppleBadge} alt="" className="hero-store-btn-icon" />
                    <span className="hero-store-btn-text">
                      <span className="hero-store-btn-small">Download the</span>
                      <span className="hero-store-btn-main">App Store</span>
                    </span>
                  </a>
                  <a
                    href={googlePlayUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hero-store-btn cta-store-btn"
                    aria-label="Get it on Google Play"
                  >
                    <img src={heroGoogleBadge} alt="" className="hero-store-btn-icon" />
                    <span className="hero-store-btn-text">
                      <span className="hero-store-btn-small">GET IT ON</span>
                      <span className="hero-store-btn-main">Google Play</span>
                    </span>
                  </a>
                </div>

                <p className="cta-platforms">iOS . Android . Apple Silicon (M1+)</p>

                <div className="cta-brand" aria-label="Sweet Dopamine">
                  <img src={heroLogo} alt="" className="cta-brand-icon" />
                  <span className="cta-brand-text">SWEETDOPAMINE</span>
                </div>

                <div className="cta-links-row" aria-label="Footer links">
                  <nav className="cta-links">
                    <a href="#features">Features</a>
                    <span className="cta-sep" aria-hidden="true">
                      |
                    </span>
                    <a href="#how-it-works">HowIt Works</a>
                    <span className="cta-sep" aria-hidden="true">
                      |
                    </span>
                    <a href="#economy">Economy</a>
                    <span className="cta-sep" aria-hidden="true">
                      |
                    </span>
                    <a href="#incubated">NTIIncubated</a>
                    <a
                      href="https://x.com/myDopamineApp"
                      target="_blank"
                      rel="noreferrer"
                      className="cta-x cta-x--inline"
                      aria-label="Open Sweet Dopamine on X"
                    >
                      <img src={xIcon} alt="" />
                    </a>
                  </nav>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>
      <nav className="carousel-dots" aria-label="Sections">
        {slideOrder.map((id, idx) => (
          <button
            key={id}
            type="button"
            className={`carousel-dot${idx === activeSlide ? ' is-active' : ''}`}
            aria-label={`Go to ${id.replace(/-/g, ' ')}`}
            aria-current={idx === activeSlide ? 'true' : undefined}
            onClick={() => onCarouselDotClick(id, idx)}
          />
        ))}
      </nav>
      {activeEmbedUrl && (
        <div className="video-modal-overlay" role="presentation" onClick={() => closeVideoModal('overlay')}>
          <div className="video-modal" role="dialog" aria-modal="true" aria-label="Demo video modal" onClick={onVideoModalClick}>
            <button type="button" className="video-modal-close" onClick={() => closeVideoModal('button')} aria-label="Close video modal">
              ×
            </button>
            <div className="video-modal-frame-wrap">
              <iframe
                src={activeEmbedUrl}
                title="Sweet Dopamine demo video"
                className="video-modal-frame"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
